import React, { useState } from 'react';
import { 
  Card, 
  Button, 
  Input, 
  Typography, 
  Alert, 
  Space, 
  List,
  Divider,
  message 
} from 'antd';
import { 
  QrcodeOutlined, 
  SafetyOutlined, 
  CopyOutlined,
  DownloadOutlined 
} from '@ant-design/icons';
import { authApi } from '../../../api/auth';

const { Title, Text, Paragraph } = Typography;

interface MFASetupProps {
  onMFAStatusChange: () => void;
}

const MFASetup: React.FC<MFASetupProps> = ({ onMFAStatusChange }) => {
  const [step, setStep] = useState<'setup' | 'verify' | 'complete'>('setup');
  const [loading, setLoading] = useState(false);
  const [setupData, setSetupData] = useState<any>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [backupTokens, setBackupTokens] = useState<string[]>([]);

  const initializeSetup = async () => {
    setLoading(true);
    try {
      const response = await authApi.mfaSetup();
      setSetupData(response);
      setStep('verify');
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Erreur lors de l\'initialisation de l\'A2F');
    } finally {
      setLoading(false);
    }
  };

  const verifyAndEnable = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      message.error('Veuillez saisir un code à 6 chiffres valide');
      return;
    }

    setLoading(true);
    try {
      const response = await authApi.mfaEnable({ otp_token: verificationCode });
      setBackupTokens(response.backup_tokens);
      setStep('complete');
      message.success('Authentification à deux facteurs activée avec succès !');
      onMFAStatusChange();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Code de vérification invalide');
    } finally {
      setLoading(false);
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(setupData.secret);
    message.success('Clé secrète copiée dans le presse-papiers');
  };

  const downloadBackupTokens = () => {
    const content = `Codes de sauvegarde TinsaFlow A2F\n\nGénérés le : ${new Date().toLocaleString('fr-FR')}\n\n${backupTokens.join('\n')}\n\nConservez ces codes dans un endroit sûr. Chaque code ne peut être utilisé qu'une seule fois.`;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'tinsaflow-codes-sauvegarde.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (step === 'setup') {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <SafetyOutlined style={{ fontSize: '48px', color: '#52c41a', marginBottom: '16px' }} />
          <Title level={3}>Activer l'authentification à deux facteurs</Title>
          <Paragraph>
            Renforcez la sécurité de votre compte en activant l'authentification à deux facteurs. 
            Vous aurez besoin d'une application d'authentification comme Google Authenticator ou Authy.
          </Paragraph>
          <Button 
            type="primary" 
            size="large" 
            onClick={initializeSetup}
            loading={loading}
          >
            Commencer
          </Button>
        </div>
      </Card>
    );
  }

  if (step === 'verify') {
    return (
      <Card title="Configuration de l'authentification à deux facteurs">
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <Title level={4}>Étape 1 : Scanner le code QR</Title>
            <Paragraph>
              Scannez ce code QR avec votre application d'authentification (Google Authenticator, Authy, etc.)
            </Paragraph>
            <div 
              style={{ 
                textAlign: 'center', 
                padding: '20px',
                background: '#fafafa',
                borderRadius: '8px',
                marginBottom: '16px'
              }}
              dangerouslySetInnerHTML={{ __html: setupData.qr_code }}
            />
          </div>

          <div>
            <Title level={4}>Étape 2 : Saisie manuelle (Optionnel)</Title>
            <Paragraph>
              Ou saisissez manuellement cette clé secrète dans votre application d'authentification :
            </Paragraph>
            <Input.Group compact>
              <Input 
                value={setupData.secret} 
                readOnly 
                style={{ width: 'calc(100% - 40px)' }}
              />
              <Button icon={<CopyOutlined />} onClick={copySecret} />
            </Input.Group>
          </div>

          <div>
            <Title level={4}>Étape 3 : Vérifier la configuration</Title>
            <Paragraph>
              Saisissez le code à 6 chiffres de votre application d'authentification pour terminer la configuration :
            </Paragraph>
            <Space>
              <Input
                placeholder="Saisir le code à 6 chiffres"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                style={{ width: '200px' }}
                maxLength={6}
              />
              <Button 
                type="primary"
                onClick={verifyAndEnable}
                loading={loading}
                disabled={verificationCode.length !== 6}
              >
                Vérifier et activer
              </Button>
            </Space>
          </div>
        </Space>
      </Card>
    );
  }

  if (step === 'complete') {
    return (
      <Card title="Authentification à deux facteurs activée">
        <Alert
          message="A2F activée avec succès !"
          description="Votre compte est maintenant protégé par l'authentification à deux facteurs."
          type="success"
          style={{ marginBottom: '24px' }}
        />
        
        <Title level={4}>Codes de sauvegarde</Title>
        <Alert
          message="Important : Sauvegardez vos codes de récupération"
          description="Ces codes peuvent être utilisés pour accéder à votre compte si vous perdez votre appareil d'authentification. Chaque code ne peut être utilisé qu'une seule fois."
          type="warning"
          style={{ marginBottom: '16px' }}
        />
        
        <List
          size="small"
          dataSource={backupTokens}
          renderItem={(token) => (
            <List.Item>
              <Text code style={{ fontSize: '16px' }}>{token}</Text>
            </List.Item>
          )}
          style={{ 
            background: '#fafafa', 
            padding: '16px', 
            borderRadius: '8px',
            marginBottom: '16px'
          }}
        />
        
        <Space>
          <Button 
            type="primary"
            icon={<DownloadOutlined />}
            onClick={downloadBackupTokens}
          >
            Télécharger les codes de sauvegarde
          </Button>
          <Button onClick={() => setStep('setup')}>
            Terminé
          </Button>
        </Space>
      </Card>
    );
  }

  return null;
};

export default MFASetup;